import { useCallback, useEffect, useMemo, useState } from 'react';
import { callApi } from '../lib/api.ts';
import { StatusChip, type ChipTone } from './StatusChip.tsx';
import { BuildStatus } from './BuildStatus.tsx';
import { transformForAction, type Ol5TransformKind } from '../lib/ol5.ts';
import { friendlyError } from '../lib/friendlyError.ts';
import { btnGhost, btnSolid, btnSolidSm, btnGhostSm, btnDangerSm, input as inputCls, card } from '../lib/ui.ts';
import { FlapText } from './FlapText.tsx';

/**
 * OL-2 (optimisation-loop.md) — the FLEET-BATCHED approval queue. ALL clients' AI-proposed
 * actions in ONE list, grouped by client, so Neil triages the whole fleet in a sitting (the
 * scaling lever). Per-row: Approve · Reject · Drop · Mark done · edit · reorder. INTERNAL —
 * never client-facing, never a site write. Reads actions-fleet, mutates via actions-update.
 */

type Level = 'high' | 'med' | 'low';
type ExecutionMode = 'templated' | 'bespoke';
type ActionStatus = 'proposed' | 'approved' | 'rejected' | 'done' | 'dropped';

interface FleetAction {
  id: string;
  clientId: string;
  clientName: string;
  clientSlug: string;
  clientIsLive: boolean;
  period: string;
  rank: number;
  title: string;
  description: string;
  target: string | null;
  impact: Level;
  effort: Level;
  executionMode: ExecutionMode;
  status: ActionStatus;
  source: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ActionEdit {
  title?: string;
  description?: string;
  target?: string | null;
  impact?: Level;
  effort?: Level;
  executionMode?: ExecutionMode;
}

const STATUS_TONE: Record<ActionStatus, ChipTone> = {
  proposed: 'info',
  approved: 'success',
  rejected: 'danger',
  done: 'success',
  dropped: 'neutral',
};

export function ActionQueue() {
  const [actions, setActions] = useState<FleetAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);

  // Filters. Default = the proposals awaiting a decision (the point of the queue).
  const [showApproved, setShowApproved] = useState(false);
  const [execFilter, setExecFilter] = useState<'all' | ExecutionMode>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const statuses: ActionStatus[] = showApproved ? ['proposed', 'approved'] : ['proposed'];
      const r = await callApi<{ actions: FleetAction[] }>('actions-fleet', {
        method: 'POST',
        body: JSON.stringify({ statuses }),
      });
      setActions(r.actions);
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setLoading(false);
    }
  }, [showApproved]);

  useEffect(() => {
    void load();
  }, [load]);

  async function mutate(id: string, body: { status?: ActionStatus; rank?: number; edit?: ActionEdit }) {
    setBusyId(id);
    setErr(null);
    try {
      await callApi<{ action: FleetAction }>('actions-update', {
        method: 'POST',
        body: JSON.stringify({ id, ...body }),
      });
      await load();
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setBusyId(null);
    }
  }

  // Client-side filters over the fleet list the endpoint already ordered (client → rank).
  const filtered = useMemo(
    () =>
      actions.filter(
        (a) =>
          (execFilter === 'all' || a.executionMode === execFilter) &&
          (clientFilter === 'all' || a.clientId === clientFilter),
      ),
    [actions, execFilter, clientFilter],
  );

  // Group by client, preserving the endpoint's order (already client → rank).
  const groups = useMemo(() => {
    const map = new Map<string, { name: string; slug: string; isLive: boolean; items: FleetAction[] }>();
    for (const a of filtered) {
      const g = map.get(a.clientId) ?? { name: a.clientName, slug: a.clientSlug, isLive: a.clientIsLive, items: [] };
      g.items.push(a);
      map.set(a.clientId, g);
    }
    return [...map.entries()].map(([clientId, g]) => ({ clientId, ...g }));
  }, [filtered]);

  const clientOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const a of actions) if (!seen.has(a.clientId)) seen.set(a.clientId, a.clientName);
    return [...seen.entries()].sort((x, y) => x[1].localeCompare(y[1]));
  }, [actions]);

  // Reorder within a client group: swap this row's rank with its neighbour's (two updates).
  async function move(group: FleetAction[], index: number, dir: -1 | 1) {
    const a = group[index];
    const b = group[index + dir];
    if (!a || !b) return;
    setBusyId(a.id);
    setErr(null);
    try {
      await callApi('actions-update', { method: 'POST', body: JSON.stringify({ id: a.id, rank: b.rank }) });
      await callApi('actions-update', { method: 'POST', body: JSON.stringify({ id: b.id, rank: a.rank }) });
      await load();
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setBusyId(null);
    }
  }

  // OL-5 PR4 — per-action auto-execute flow (approved templated → Apply → green preview → Go live).
  // Keyed by action id; ephemeral (reload clears them, matching the queue's stateless model).
  const [applyBuild, setApplyBuild] = useState<Record<string, string>>({});
  const [previewReady, setPreviewReady] = useState<Record<string, boolean>>({});
  const [goliveBuild, setGoliveBuild] = useState<Record<string, string>>({});

  async function applyAction(a: FleetAction, transform: Ol5TransformKind) {
    setBusyId(a.id);
    setErr(null);
    try {
      const r = await callApi<{ buildId: string }>('ol5-apply', {
        method: 'POST',
        body: JSON.stringify({ clientId: a.clientId, actionId: a.id, transform }),
      });
      setApplyBuild((m) => ({ ...m, [a.id]: r.buildId }));
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setBusyId(null);
    }
  }

  async function goliveAction(a: FleetAction) {
    setBusyId(a.id);
    setErr(null);
    try {
      const r = await callApi<{ buildId: string }>('ol5-golive', {
        method: 'POST',
        body: JSON.stringify({ clientId: a.clientId, actionId: a.id }),
      });
      setGoliveBuild((m) => ({ ...m, [a.id]: r.buildId }));
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
    } finally {
      setBusyId(null);
    }
  }

  // Batch trust-step: apply EVERY loaded approved+templated+mappable action that hasn't started yet.
  // Each row then drives its own preview poll + per-row Go-live (the green gate is never batched).
  const batchApplicable = filtered.filter(
    (a) => a.status === 'approved' && a.executionMode === 'templated' && transformForAction(a) && !applyBuild[a.id],
  );
  async function applyAllApproved() {
    setErr(null);
    for (const a of batchApplicable) {
      const kind = transformForAction(a);
      if (kind) await applyAction(a, kind);
    }
  }

  const proposedCount = actions.filter((a) => a.status === 'proposed').length;

  return (
    <section className={`${card} space-y-5 p-5`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-ink">Action queue</h2>
          <p className="text-[11px] text-ink-2">
            AI-proposed optimisations across the whole fleet — approve, reject or reorder in one sitting; auto-apply a templated action to its preview, then Go live (green-gated). Internal only; never seen by clients.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-ink-2">
          <label className="flex items-center gap-1" title="Also show the approved plan (not just pending proposals)">
            <input type="checkbox" checked={showApproved} onChange={(e) => setShowApproved(e.target.checked)} />
            show approved
          </label>
          <label className="flex items-center gap-1">
            Mode
            <select value={execFilter} onChange={(e) => setExecFilter(e.target.value as typeof execFilter)} className={inputCls}>
              <option value="all">all</option>
              <option value="templated">Automatic</option>
              <option value="bespoke">Hand-built</option>
            </select>
          </label>
          <label className="flex items-center gap-1">
            Client
            <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className={inputCls}>
              <option value="all">all</option>
              {clientOptions.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          {batchApplicable.length > 0 && (
            <button
              onClick={() => void applyAllApproved()}
              disabled={!!busyId}
              className={btnSolid}
              title="Auto-apply every approved templated action to its preview (Go-live stays per-row + green-gated)"
            >
              <FlapText>Apply all approved templated ({batchApplicable.length})</FlapText>
            </button>
          )}
          <button onClick={() => void load()} disabled={loading} className={btnGhost}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {err && <p className="text-sm text-danger">{err}</p>}

      {!loading && groups.length === 0 && (
        <p className="text-sm text-ink-2">
          {proposedCount === 0
            ? 'No proposed actions right now. They appear after a quarterly report is interpreted.'
            : 'Nothing matches the current filters.'}
        </p>
      )}

      <div className="space-y-5">
        {groups.map((g) => (
          <div key={g.clientId} className="space-y-2">
            <div className="flex items-baseline gap-2 border-b border-line pb-1">
              <span className="text-sm font-medium text-ink">{g.name}</span>
              <span className="font-mono text-[11px] text-ink-2">{g.slug}</span>
              {g.isLive && <StatusChip tone="success">live</StatusChip>}
              <span className="text-[11px] text-ink-2">
                {g.items.length} action{g.items.length === 1 ? '' : 's'}
              </span>
            </div>
            {g.items.map((a, i) => (
              <ActionRow
                key={a.id}
                a={a}
                busy={busyId === a.id}
                editing={editId === a.id}
                canMoveUp={i > 0}
                canMoveDown={i < g.items.length - 1}
                onMove={(dir) => void move(g.items, i, dir)}
                onEdit={() => setEditId(a.id)}
                onCancelEdit={() => setEditId(null)}
                onSaveEdit={async (edit) => {
                  await mutate(a.id, { edit });
                  setEditId(null);
                }}
                onStatus={(status) => void mutate(a.id, { status })}
                transformKind={transformForAction(a)}
                applyBuildId={applyBuild[a.id]}
                onApply={(kind) => void applyAction(a, kind)}
                previewReady={!!previewReady[a.id]}
                onPreviewReady={() => setPreviewReady((m) => ({ ...m, [a.id]: true }))}
                goliveBuildId={goliveBuild[a.id]}
                onGolive={() => void goliveAction(a)}
                onLive={() => void load()}
              />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

const LEVEL_TONE: Record<Level, ChipTone> = { high: 'info', med: 'neutral', low: 'neutral' };

function ActionRow({
  a,
  busy,
  editing,
  canMoveUp,
  canMoveDown,
  onMove,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onStatus,
  transformKind,
  applyBuildId,
  onApply,
  previewReady,
  onPreviewReady,
  goliveBuildId,
  onGolive,
  onLive,
}: {
  a: FleetAction;
  busy: boolean;
  editing: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (dir: -1 | 1) => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (edit: ActionEdit) => Promise<void>;
  onStatus: (status: ActionStatus) => void;
  transformKind: Ol5TransformKind | null;
  applyBuildId?: string;
  onApply: (kind: Ol5TransformKind) => void;
  previewReady: boolean;
  onPreviewReady: () => void;
  goliveBuildId?: string;
  onGolive: () => void;
  onLive: () => void;
}) {
  return (
    <div className="rounded border border-line bg-paper-2 p-3">
      <div className="flex items-start gap-3">
        {/* rank + reorder */}
        <div className="flex flex-col items-center gap-0.5 pt-0.5">
          <button
            onClick={() => onMove(-1)}
            disabled={busy || !canMoveUp}
            className="text-ink-2 hover:text-ink disabled:opacity-30"
            title="Move up (higher priority)"
            aria-label="Move up"
          >
            ▲
          </button>
          <span className="font-mono text-xs text-ink-2" title="Priority rank (1 = highest leverage)">
            {a.rank}
          </span>
          <button
            onClick={() => onMove(1)}
            disabled={busy || !canMoveDown}
            className="text-ink-2 hover:text-ink disabled:opacity-30"
            title="Move down (lower priority)"
            aria-label="Move down"
          >
            ▼
          </button>
        </div>

        <div className="min-w-0 flex-1">
          {editing ? (
            <EditForm a={a} busy={busy} onCancel={onCancelEdit} onSave={onSaveEdit} />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-ink">{a.title}</span>
                <StatusChip tone={STATUS_TONE[a.status]}>{a.status}</StatusChip>
              </div>
              <p className="mt-0.5 text-xs text-ink-2">{a.description}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                {a.target && <span className="font-mono text-ink-2">{a.target}</span>}
                <StatusChip tone={LEVEL_TONE[a.impact]}>impact {a.impact}</StatusChip>
                <StatusChip tone={a.effort === 'low' ? 'success' : a.effort === 'high' ? 'warning' : 'neutral'}>
                  effort {a.effort}
                </StatusChip>
                <StatusChip tone={a.executionMode === 'templated' ? 'success' : 'warning'}>
                  {a.executionMode === 'templated' ? 'Automatic' : 'Hand-built'}
                </StatusChip>
                {a.source && <StatusChip tone="neutral">from {a.source}</StatusChip>}
              </div>
            </>
          )}
        </div>

        {/* actions */}
        {!editing && (
          <div className="flex shrink-0 flex-col items-stretch gap-1">
            {a.status === 'proposed' && (
              <>
                <button onClick={() => onStatus('approved')} disabled={busy} className={btnSolidSm}>
                  <FlapText>Approve</FlapText>
                </button>
                <button onClick={() => onStatus('rejected')} disabled={busy} className={btnDangerSm}>
                  Reject
                </button>
                <button onClick={onEdit} disabled={busy} className={btnGhostSm}>
                  Edit
                </button>
                <button onClick={() => onStatus('dropped')} disabled={busy} className={btnGhostSm} title="Set aside without rejecting">
                  Drop
                </button>
              </>
            )}
            {a.status === 'approved' && (
              <>
                {/* OL-5: auto-execute a templated action (Apply → green preview → Go live → done). */}
                {transformKind && !applyBuildId && (
                  <button
                    onClick={() => onApply(transformKind)}
                    disabled={busy}
                    className={btnSolidSm}
                    title="Make this automatic fix and show it on the preview"
                  >
                    <FlapText>Apply automatic fix</FlapText>
                  </button>
                )}
                {applyBuildId && (
                  <div className="flex items-center gap-1 text-[11px] text-ink-2">
                    <span>preview</span>
                    <BuildStatus buildId={applyBuildId} initialUrl={null} live endpoint="ol5-status" onResolved={onPreviewReady} />
                  </div>
                )}
                {applyBuildId && !goliveBuildId && (
                  <button
                    onClick={onGolive}
                    disabled={busy || !previewReady}
                    className={btnSolidSm}
                    title={previewReady ? 'Make this change live' : 'Waiting for the preview to go green'}
                  >
                    <FlapText>{previewReady ? 'Go live' : 'Go live (preview building…)'}</FlapText>
                  </button>
                )}
                {goliveBuildId && (
                  <div className="flex items-center gap-1 text-[11px] text-ink-2">
                    <span>live</span>
                    <BuildStatus buildId={goliveBuildId} initialUrl={null} live endpoint="promote-status" readyLabel="Live ✅" openLabel="open live ↗" onResolved={onLive} />
                  </div>
                )}
                {/* Manual fallback: mark done by hand (bespoke, an unmapped templated action, or a
                    change done outside the app). Auto-execute flips done itself after go-live. */}
                {!goliveBuildId && (
                  <button onClick={() => onStatus('done')} disabled={busy} className={btnGhostSm} title="Mark this work as landed (manual)">
                    Mark done
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function EditForm({
  a,
  busy,
  onCancel,
  onSave,
}: {
  a: FleetAction;
  busy: boolean;
  onCancel: () => void;
  onSave: (edit: ActionEdit) => Promise<void>;
}) {
  const [title, setTitle] = useState(a.title);
  const [description, setDescription] = useState(a.description);
  const [target, setTarget] = useState(a.target ?? '');
  const [impact, setImpact] = useState<Level>(a.impact);
  const [effort, setEffort] = useState<Level>(a.effort);
  const [executionMode, setExecutionMode] = useState<ExecutionMode>(a.executionMode);

  return (
    <div className="space-y-2">
      <input value={title} onChange={(e) => setTitle(e.target.value)} className={`${inputCls} w-full`} placeholder="Title" />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        className={`${inputCls} w-full`}
        placeholder="What to do + the real finding it addresses"
      />
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-2">
        <input value={target} onChange={(e) => setTarget(e.target.value)} className={`${inputCls} font-mono`} placeholder="/target (blank = site-wide)" />
        <label className="flex items-center gap-1">
          impact
          <select value={impact} onChange={(e) => setImpact(e.target.value as Level)} className={inputCls}>
            <option value="high">high</option>
            <option value="med">med</option>
            <option value="low">low</option>
          </select>
        </label>
        <label className="flex items-center gap-1">
          effort
          <select value={effort} onChange={(e) => setEffort(e.target.value as Level)} className={inputCls}>
            <option value="high">high</option>
            <option value="med">med</option>
            <option value="low">low</option>
          </select>
        </label>
        <label className="flex items-center gap-1">
          mode
          <select value={executionMode} onChange={(e) => setExecutionMode(e.target.value as ExecutionMode)} className={inputCls}>
            <option value="templated">Automatic</option>
            <option value="bespoke">Hand-built</option>
          </select>
        </label>
      </div>
      <div className="flex gap-2">
        <button
          disabled={busy}
          className={btnSolid}
          onClick={() =>
            void onSave({ title, description, target: target.trim() || null, impact, effort, executionMode })
          }
        >
          <FlapText>{busy ? 'Saving…' : 'Save'}</FlapText>
        </button>
        <button disabled={busy} className={btnGhost} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
